// Answer controls, Direction C: rounded rows and pills, a round slider thumb, a radio ring.
import { useRef, type PointerEvent } from "react";
import type { Choice, ChoiceOption, DomainCode } from "../lib/types";

/** A4: four stacked rows; the chosen one is outlined and tinted in Leaf. */
export function AgreeScale({
  options,
  value,
  onPick,
}: {
  options: ChoiceOption[];
  value: Choice | undefined;
  onPick: (v: Choice) => void;
}) {
  return (
    <div role="group" aria-label="Your answer" className="flex flex-col gap-2.5">
      {options.map((o) => {
        const selected = value === o.value;
        return (
          <button
            key={String(o.value)}
            type="button"
            aria-pressed={selected}
            onClick={() => onPick(o.value)}
            className={`min-h-[58px] rounded-[16px] border px-5 text-left text-[17px] font-medium ${
              selected ? "border-chosen bg-chosen-tint text-ink ring-1 ring-chosen" : "border-line bg-panel text-ink hover:bg-soft"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Centre of stop i (of n) along the track, leaving room for the 34px thumb at each end. */
const stopLeft = (i: number, n: number) => `calc(17px + (100% - 34px) * ${n > 1 ? i / (n - 1) : 0})`;

/**
 * F4: a four-stop slider. Built from a radio group so it works with a keyboard and a screen
 * reader and can show "not answered yet" (a native range always has a value); the track
 * also takes taps and drags. The round thumb stays hidden until the first pick.
 */
export function FrequencySlider({
  name,
  options,
  value,
  onPick,
}: {
  name: string;
  options: ChoiceOption[];
  value: Choice | undefined;
  onPick: (v: Choice) => void;
}) {
  const track = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const index = options.findIndex((o) => o.value === value);
  const touched = index >= 0;
  const n = options.length;

  const pickAt = (clientX: number) => {
    const el = track.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 34) return;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left - 17) / (rect.width - 34)));
    const o = options[Math.round(ratio * (n - 1))];
    if (o && o.value !== value) onPick(o.value);
  };
  const onPointerDown = (e: PointerEvent) => {
    dragging.current = true;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent) => {
    if (dragging.current) pickAt(e.clientX);
  };
  const onPointerUp = (e: PointerEvent) => {
    if (dragging.current) pickAt(e.clientX);
    dragging.current = false;
  };

  return (
    <fieldset className="m-0 border-0 p-0">
      <legend className="sr-only">How often</legend>
      <div
        ref={track}
        className="relative h-[34px] touch-none select-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => (dragging.current = false)}
      >
        <div aria-hidden="true" className="absolute top-[13px] right-0 left-0 h-2 rounded-full bg-line" />
        {touched ? (
          <span
            aria-hidden="true"
            data-testid="slider-thumb"
            className="absolute top-0 -ml-[17px] h-[34px] w-[34px] rounded-full border-[5px] border-ground bg-accent transition-[left]"
            style={{ left: stopLeft(index, n), boxShadow: "0 0 0 1px var(--color-accent)" }}
          />
        ) : null}
      </div>
      <div className="relative mt-2 h-12">
        {options.map((o, i) => {
          const checked = i === index;
          const first = i === 0;
          const last = i === n - 1;
          return (
            <label
              key={String(o.value)}
              className={`absolute top-0 flex min-h-11 cursor-pointer items-start rounded-[10px] px-1 pt-1 text-[13px] leading-tight has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-accent ${
                checked ? "font-semibold text-ink" : "text-muted"
              } ${first ? "left-0 text-left" : last ? "right-0 max-w-[5.5rem] text-right" : "-translate-x-1/2 text-center whitespace-nowrap"}`}
              style={first || last ? undefined : { left: stopLeft(i, n) }}
            >
              <input
                type="radio"
                className="sr-only-input"
                name={name}
                value={String(o.value)}
                checked={checked}
                onChange={() => onPick(o.value)}
              />
              {o.label}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/** YN / TF: two tall cards with serif labels. */
export function BinaryChoice({
  options,
  value,
  onPick,
}: {
  options: ChoiceOption[];
  value: Choice | undefined;
  onPick: (v: Choice) => void;
}) {
  return (
    <div role="group" aria-label="Your answer" className="grid grid-cols-2 gap-3">
      {options.map((o) => {
        const selected = value === o.value;
        return (
          <button
            key={String(o.value)}
            type="button"
            aria-pressed={selected}
            onClick={() => onPick(o.value)}
            className={`serif h-[168px] rounded-[24px] border text-[36px] ${
              selected ? "border-chosen bg-chosen-tint text-ink ring-1 ring-chosen" : "border-line bg-panel text-ink hover:bg-soft"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** MC4: a list of rows with a radio ring, in the order the API sends (shuffled per person). */
export function PickOne({
  options,
  value,
  onPick,
}: {
  options: { value: DomainCode; text: string }[];
  value: Choice | undefined;
  onPick: (v: Choice) => void;
}) {
  return (
    <div role="group" aria-label="Your answer" className="flex flex-col gap-2.5">
      {options.map((o) => {
        const selected = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={selected}
            onClick={() => onPick(o.value)}
            className={`flex min-h-16 items-center gap-4 rounded-[16px] border px-[18px] py-3 text-left text-[17px] font-medium text-ink ${
              selected ? "border-chosen bg-chosen-tint ring-1 ring-chosen" : "border-line bg-panel hover:bg-soft"
            }`}
          >
            <span
              aria-hidden="true"
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 ${
                selected ? "border-chosen" : "border-muted/60"
              }`}
            >
              <span className={`h-3 w-3 rounded-full ${selected ? "bg-chosen" : "bg-transparent"}`} />
            </span>
            <span className="min-w-0">{o.text}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Back: a 56px circle with a chevron. */
export function BackButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label="Back"
      className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-line bg-panel text-ink disabled:opacity-40"
    >
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 18l-6-6 6-6" />
      </svg>
    </button>
  );
}
